import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { Plus } from 'lucide-react'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Smartrade } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { AddSecurityEntryModal } from '@/components/dialogs/AddSecurityEntryModal'
import { SMARTRADE } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { useUpdateSecurityEntryMutation } from '@/react-query/mutations/smartrade'

const postgresColDefs = (hasWriteAccess: boolean) =>
  createColDefs([
    {
      field: 'isin',
      width: 115,
      pinned: 'left',
    },
    {
      field: 'sedol',
      width: 100,
    },
    {
      field: 'currency',
      width: 95,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'description',
      width: 250,
    },
    {
      field: 'counterparty',
      headerName: 'Issuer',
      width: 150,
    },
    {
      field: 'denominations',
      headerName: 'Unit',
      width: 85,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'startdripdate',
      headerName: 'Drip Start',
      width: 115,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'enddripdate',
      headerName: 'Drip End',
      width: 115,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'startdripamount',
      headerName: 'Drip Bps',
      width: 115,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'enddripamount',
      headerName: 'Drip Bps End',
      width: 115,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'tofix',
      width: 100,
      cellStyle: { textAlign: 'center' },
      editable: hasWriteAccess,
    },
  ])

const mySQLColDefs = createColDefs([
  {
    field: 'isin',
    width: 115,
    pinned: 'left',
  },
  {
    field: 'sedol',
    width: 100,
  },
  {
    field: 'currency',
    width: 95,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'description',
    width: 250,
  },
  {
    field: 'Bank',
    headerName: 'Issuer',
    width: 150,
  },
  {
    field: 'unit',
    width: 85,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'supportstart',
    headerName: 'Drip Start',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'supportend',
    headerName: 'Drip End',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'fortemofferpnlstart',
    headerName: 'Drip Bps',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'fortemofferpnlend',
    headerName: 'Drip Bps End',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'notionalLimit',
    width: 125,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  },
])

export interface SmartradeDataPageProps {}

export const SmartradeDataPage: React.FC<SmartradeDataPageProps> = ({}) => {
  const { hasPermissions } = useUserContext()

  const [uploading, setUploading] = useState(false)

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_SMARTRADE_DATA],
  }))

  const updateSecurityEntryMutation = useUpdateSecurityEntryMutation()

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['smartrade', 'data', 'postgres'],
        queryFn: () => api.smartrade.getSecurityEntries(),
        refetchInterval: 60_000,
      },
      {
        queryKey: ['smartrade', 'data', 'mysql'],
        queryFn: () => api.smartrade.getMySQLSecurityEntries(),
        refetchInterval: 60_000,
      },
    ],
  })

  const [pg, mysql] = queries

  const [pgEntries, mySQLEntries] = useMemo(() => {
    return [pg.data ?? [], mysql?.data ?? []]
  }, [pg.data, mysql])

  return (
    <>
      <AddSecurityEntryModal open={uploading} onOpenChange={setUploading} />
      <Page metadata={{ title: 'Smartrade Data' }} permissions={SMARTRADE.DATA.permissions}>
        <CanAccess permissions={[PERMISSIONS.WRITE_SMARTRADE_DATA]}>
          <HeaderConfigBar>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={'outline'} onClick={() => setUploading(true)}>
                <Plus /> New entry
              </Button>
            </div>
          </HeaderConfigBar>
        </CanAccess>
        <ResizablePanelGroup direction={'vertical'}>
          <ResizablePanel>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery title={`Smartrade Entries (Postgres)`} query={pg} />
              <WidgetContent loading={pg.isLoading}>
                <CustomAgGrid
                  getRowId={(params: any) => params.data.isin?.toString()}
                  onCellValueChanged={(params: any) => {
                    return updateSecurityEntryMutation.mutateAsync({
                      newValue: params.newValue,
                      oldValue: params.oldValue,
                      rowData: params.data,
                      updatedField: params.colDef
                        .field as keyof Smartrade.GetSmartradeSecurityEntriesData[number],
                    })
                  }}
                  domLayout={'normal'}
                  rowData={pgEntries}
                  columnDefs={postgresColDefs(hasWriteAccess)}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery title={`Smartrade Entries (MySQL)`} query={mysql} />
              <WidgetContent loading={mysql?.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  rowData={mySQLEntries}
                  columnDefs={mySQLColDefs}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        </ResizablePanelGroup>
      </Page>
    </>
  )
}
